import { LazyExoticComponent, useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFlagValue } from '@labrav/flags'
import { useTracker, useAccountId } from '@labrav/common'
import { useSelector } from '../../store'

export type productTab = {
  key: string
  label: string
  renderLabel?: JSX.Element
  product?: string
  path: string
  component?: LazyExoticComponent<any> | React.FC
  search?: string
  renderElement?: JSX.Element
  childRoutes?: productTab[]
  hidden?: boolean
}

const getRouteMatchMapper = (
  cosellCloudSettingsImpartner: boolean
): Record<string, string> => {
  const baseMapper: Record<string, string> = {
    '*': '',
    'oppsync/*': '/oppsync',
    'flyout/*': '/flyout',
    'cloudfaas/*': 'cloudfaas',
    'app-settings': '/app-settings',
    crm: '/crm',
    users: '/users',
    keys: '/keys',
    integrations: '/integrations',
    cloud: '/cloud',
    notifications: '/notifications',
    mappings: '/mappings',
    cloudAccounts: '/cloud-accounts',
    sync: '/sync',
    'labra/*': '/labra',
    sales: '/sales',
    productsListing: '/productsListing',
    syncs: '/syncs',
    'private-offers': '/private-offers',
    'channel-offers': '/channel-offers',
    'resale-authorizations': '/resale-authorizations',
    'incoming-referrals': 'incoming-referrals',
    'outgoing-referrals': 'outgoing-referrals',
    'incoming-leads': 'incoming-leads',
    warnings: 'warnings',
    'admin-settings': '/admin-settings',
    intelligence: '/intelligence',
    'crm-audit-log': '/crm-audit-log',
    sent: '/sent',
    received: '/received',
    'co-sell': 'co-sell',
    marketplace: 'marketplace',
    aws: '/aws',
    microsoft: '/microsoft',
    'storefront-metrics': '/storefront-metrics',
    storefront: '/storefront',
    ':cloud/:accountId/cosell/*': '/cosell',
    'flyout/cloud/:cloudType/:accountId/*': '/flyout/cloud/',
    'flyout/cloud': '/flyout/cloud',
  }

  if (cosellCloudSettingsImpartner) {
    baseMapper['oppsync/cloud/:cloudType/:accountId/*'] = '/oppsync/cloud/'
    baseMapper['oppsync/cloud'] = '/oppsync/cloud'
  }

  return baseMapper
}

const isDynamicCosellRoute = (path: string): boolean =>
  path.includes(':cloud') &&
  path.includes(':accountId') &&
  path.includes('cosell')

const findActiveTabIndex = (
  tabs: productTab[],
  pathname: string,
  routeMatchMapper: Record<string, string>
): number => {
  const reversedTabs = [...tabs].reverse()

  let reversedIndex = reversedTabs.findIndex(tab => {
    if (tab.path === '*') return false
    if (isDynamicCosellRoute(tab.path)) return pathname.includes('/cosell')

    const routeMatch = routeMatchMapper[tab.path]
    return routeMatch && pathname.includes(routeMatch)
  })

  if (reversedIndex === -1) {
    reversedIndex = reversedTabs.findIndex(tab => tab.path === '*')
  }

  return reversedIndex === -1 ? -1 : Math.abs(reversedIndex - tabs.length) - 1
}

export const settingsTabs = (
  productTabs: productTab[],
  defaultTabs: productTab[],
  shouldRedirectOnChange?: boolean,
  track?: boolean,
  eventHeading?: string
) => {
  const { value: cosellCloudSettingsImpartner } = useFlagValue(
    'cosellCloudSettingsImpartner'
  )
  const routeMatchMapper = getRouteMatchMapper(
    cosellCloudSettingsImpartner ?? false
  )
  const allSettingsTabs = defaultTabs.concat(productTabs)
  const defaultSettingsTabs = allSettingsTabs.filter(tab => !tab.hidden)
  const sellerName = useSelector<string | undefined>(
    state => state.flyoutSellerInfo.awsAccountName
  )
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const tracker = useTracker()
  const accountId = useAccountId({ product: 'flyout' }) || ''

  const trackEvent = async (heading: string, subHeading: string) => {
    tracker?.track({
      customTracking: true,
      section: heading.replace(/\s+/g, '-'),
      component: subHeading.replace(/\s+/g, '-'),
      action: 'clicked',
      title: `${subHeading} viewed`,
      source: `${subHeading} page`,
      partnerId: accountId,
      customerName: sellerName || '',
    })
  }

  const activeTabIndexFromRoute = findActiveTabIndex(
    defaultSettingsTabs,
    pathname,
    routeMatchMapper
  )

  const [activeTab, selectTab] = useState(
    activeTabIndexFromRoute < 0 ? 0 : activeTabIndexFromRoute
  )

  const handleTabChange = useCallback(
    (index: number) => {
      selectTab(index)
      if (shouldRedirectOnChange) {
        if (track && eventHeading) {
          trackEvent(eventHeading, defaultSettingsTabs[index].label)
        }
        navigate(
          {
            pathname: defaultSettingsTabs[index].path,
            search: defaultSettingsTabs[index].search || '',
          },
          { replace: true }
        )
      }
    },
    [defaultSettingsTabs]
  )

  const settingsTabsForRouting = [...allSettingsTabs].reverse()

  useEffect(() => {
    selectTab(activeTabIndexFromRoute > 0 ? activeTabIndexFromRoute : 0)
    if (activeTabIndexFromRoute === -1) {
      navigate(defaultSettingsTabs[0].path, { replace: true })
    }
  }, [activeTabIndexFromRoute])

  return {
    handleTabChange,
    settingsTabsForRouting,
    activeTab,
    defaultSettingsTabs,
  }
}
